import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import {
  ApprovalRequestDetail,
  ApprovalRequestListRow,
  ApprovalRequestStep,
  ApprovalRequestTableDto,
  ForwardCandidate,
} from '../../models/approval/approval-request.model';

@Injectable({ providedIn: 'root' })
export class MapperApprovalRequestService {
  mapTable(dto: ApprovalRequestTableDto): {
    items: ApprovalRequestListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as ApprovalRequestTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as ApprovalRequestListRow[];
    return {
      items: rows.map((row) => this.mapListRow(row as ApprovalRequestListRow & Record<string, unknown>)),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapListRow(row: ApprovalRequestListRow & Record<string, unknown>): ApprovalRequestListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      title: String(row.title ?? row['Title'] ?? ''),
      menuName: String(row.menuName ?? row['MenuName'] ?? ''),
      recordModel: String(row.recordModel ?? row['RecordModel'] ?? ''),
      recordId: Number(row.recordId ?? row['RecordId'] ?? 0),
      status: String(row.status ?? row['Status'] ?? ''),
      currentStep: row.currentStep != null ? Number(row.currentStep ?? row['CurrentStep']) : null,
      requestUserId: Number(row.requestUserId ?? row['RequestUserId'] ?? 0),
      requesterName: String(row.requesterName ?? row['RequesterName'] ?? ''),
      submittedDate: row.submittedDate != null ? String(row.submittedDate ?? row['SubmittedDate']) : null,
      completedDate: row.completedDate != null ? String(row.completedDate ?? row['CompletedDate']) : null,
      canAct: Boolean(row.canAct ?? row['CanAct'] ?? false),
      canForward: Boolean(row.canForward ?? row['CanForward'] ?? false),
    };
  }

  mapDetail(dto: ApprovalRequestDetail & Record<string, unknown>): ApprovalRequestDetail {
    const stepsRaw = (dto.steps ?? dto['Steps'] ?? []) as ApprovalRequestStep[];
    const candidatesRaw = (dto.forwardCandidates ?? dto['ForwardCandidates'] ?? []) as ForwardCandidate[];
    return {
      id: Number(dto.id ?? dto['Id'] ?? 0),
      name: String(dto.name ?? dto['Name'] ?? ''),
      title: String(dto.title ?? dto['Title'] ?? ''),
      menuListId: Number(dto.menuListId ?? dto['MenuListId'] ?? 0),
      menuName: String(dto.menuName ?? dto['MenuName'] ?? ''),
      recordModel: String(dto.recordModel ?? dto['RecordModel'] ?? ''),
      recordId: Number(dto.recordId ?? dto['RecordId'] ?? 0),
      status: String(dto.status ?? dto['Status'] ?? ''),
      currentStep: dto.currentStep != null ? Number(dto.currentStep ?? dto['CurrentStep']) : null,
      activeLevelCount: Number(dto.activeLevelCount ?? dto['ActiveLevelCount'] ?? 0),
      requestRemark: String(dto.requestRemark ?? dto['RequestRemark'] ?? ''),
      requestUserId: Number(dto.requestUserId ?? dto['RequestUserId'] ?? 0),
      requesterName: String(dto.requesterName ?? dto['RequesterName'] ?? ''),
      submittedDate: dto.submittedDate != null ? String(dto.submittedDate ?? dto['SubmittedDate']) : null,
      completedDate: dto.completedDate != null ? String(dto.completedDate ?? dto['CompletedDate']) : null,
      canAct: Boolean(dto.canAct ?? dto['CanAct'] ?? false),
      canForward: Boolean(dto.canForward ?? dto['CanForward'] ?? false),
      steps: stepsRaw.map((s) => this.mapStep(s as ApprovalRequestStep & Record<string, unknown>)),
      forwardCandidates: candidatesRaw.map((c) => this.mapForwardCandidate(c as ForwardCandidate & Record<string, unknown>)),
    };
  }

  mapStep(step: ApprovalRequestStep & Record<string, unknown>): ApprovalRequestStep {
    return {
      id: Number(step.id ?? step['Id'] ?? 0),
      stepNumber: Number(step.stepNumber ?? step['StepNumber'] ?? 0),
      stepName: String(step.stepName ?? step['StepName'] ?? ''),
      status: String(step.status ?? step['Status'] ?? ''),
      assignedUserId: step.assignedUserId != null ? Number(step.assignedUserId ?? step['AssignedUserId']) : null,
      assignedUserName: String(step.assignedUserName ?? step['AssignedUserName'] ?? ''),
      assignedRoleId: step.assignedRoleId != null ? Number(step.assignedRoleId ?? step['AssignedRoleId']) : null,
      assignedRoleName: String(step.assignedRoleName ?? step['AssignedRoleName'] ?? ''),
      assignedDepartmentId:
        step.assignedDepartmentId != null ? Number(step.assignedDepartmentId ?? step['AssignedDepartmentId']) : null,
      assignedDepartmentName: String(step.assignedDepartmentName ?? step['AssignedDepartmentName'] ?? ''),
      forwardedByUserId: step.forwardedByUserId != null ? Number(step.forwardedByUserId ?? step['ForwardedByUserId']) : null,
      forwardedByUserName: String(step.forwardedByUserName ?? step['ForwardedByUserName'] ?? ''),
      actedByUserId: step.actedByUserId != null ? Number(step.actedByUserId ?? step['ActedByUserId']) : null,
      actedByUserName: String(step.actedByUserName ?? step['ActedByUserName'] ?? ''),
      actedDate: step.actedDate != null ? String(step.actedDate ?? step['ActedDate']) : null,
      remark: String(step.remark ?? step['Remark'] ?? ''),
    };
  }

  mapForwardCandidate(item: ForwardCandidate & Record<string, unknown>): ForwardCandidate {
    return {
      userId: Number(item.userId ?? item['UserId'] ?? 0),
      userName: String(item.userName ?? item['UserName'] ?? ''),
      roleName: String(item.roleName ?? item['RoleName'] ?? ''),
      departmentName: String(item.departmentName ?? item['DepartmentName'] ?? ''),
      isDefault: Boolean(item.isDefault ?? item['IsDefault'] ?? false),
    };
  }

  getPendingStepId(detail: ApprovalRequestDetail): number | null {
    if (!detail.currentStep) return null;
    const step = detail.steps.find(
      (s) => s.stepNumber === detail.currentStep && s.status === 'pending'
    );
    return step?.id ?? null;
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      draft: 'Draft',
      pending: 'รออนุมัติ',
      awaiting_forward: 'รอเลือกผู้อนุมัติถัดไป',
      approved: 'อนุมัติแล้ว',
      rejected: 'ปฏิเสธ',
      cancelled: 'ยกเลิก',
    };
    return map[status] ?? status;
  }

  stepStatusLabel(status: string): string {
    const map: Record<string, string> = {
      pending: 'รอดำเนินการ',
      approved: 'อนุมัติแล้ว',
      rejected: 'ปฏิเสธ',
      skipped: 'ข้าม',
      delegated: 'มอบหมาย',
    };
    return map[status] ?? status;
  }

  recordModelLabel(model: string): string {
    const map: Record<string, string> = {
      city_parking_client_card_quota_request: 'คำขอโควต้าบัตร',
      city_parking_client_card_quota_allocation_request: 'คำขอจัดสรรโควต้าให้ร้าน',
      city_parking_client_card_quota_allocation: 'จัดสรรโควต้าให้ร้าน (legacy)',
    };
    return map[model] ?? model;
  }

  mapActionResponse(res: unknown): ActionBackOfficeResponse {
    const raw = (res ?? {}) as Record<string, unknown>;
    return {
      isSuccess: Boolean(raw['isSuccess'] ?? raw['IsSuccess'] ?? false),
      message: String(raw['message'] ?? raw['Message'] ?? ''),
      messageEn: String(raw['messageEn'] ?? raw['MessageEn'] ?? ''),
      code: String(raw['code'] ?? raw['Code'] ?? ''),
      IdReturn: Number(raw['IdReturn'] ?? raw['idReturn'] ?? 0),
    };
  }
}
